/* eslint-disable no-console */
import { inject, Injectable } from '@angular/core'
import { getEnumKeyFromNum, jsonMapSetStringifyReplacer } from '@pai/commons'
import { buffer, catchError, defer, filter, first, mergeMap, Observable, of, Subject, timer } from 'rxjs'

import { ClientIdService } from '../../client-id/client-id.service'
import { LogLevel } from '../log-level.enum'
import { LogRequestInfoProvider } from '../log-request-info-provider'
import { isError, pushToRingBuffer } from '../logger-helper'
import { CloudWatchLogApi, HttpApiError, LogEvent } from './cloud-watch-log.api'
import { CLOUD_WATCH_LOG_TRANSPORT_CONFIG } from './cloud-watch-log-transport-config.injection-token'
import { CloudWatchLogTransportConfig } from './cloud-watch-log-transport-config.type'

/**
 * Service to send log events to the integration api for CloudWatch Logs.
 * Handles log level filtering, ring buffer logic, and sending events to CloudWatch.
 *
 * Requires the {@link CLOUD_WATCH_LOG_TRANSPORT_CONFIG} to be provided.
 */
@Injectable()
export class CloudWatchLogger {
  private static readonly DEFAULT_BUFFER_SIZE = 100
  // max request per second
  private static readonly CLOUD_WATCH_RATE_LIMIT = 1000 / 5

  private readonly api = inject(CloudWatchLogApi)
  private readonly logsSubject = new Subject<LogEvent>()
  private readonly clientId: string
  private readonly config: CloudWatchLogTransportConfig = inject(CLOUD_WATCH_LOG_TRANSPORT_CONFIG)
  private readonly logRequestInfoProvider? = inject(LogRequestInfoProvider, { optional: true })
  private readonly jsonStringifyReplacer: (key: string, value: any) => any
  private readonly bufferSize: number

  /** Ring buffer for log events below the configured level, flushed on ERROR */
  private pendingBuffer: LogEvent[] = []

  constructor() {
    const clientIdService = inject(ClientIdService)

    this.clientId = clientIdService.clientId
    this.jsonStringifyReplacer = this.config.jsonStringifyReplacer || jsonMapSetStringifyReplacer
    this.bufferSize = this.config.bufferSize ?? CloudWatchLogger.DEFAULT_BUFFER_SIZE

    // no instantiation if LogLevel === OFF
    if (this.config.logLevel !== LogLevel.OFF) {
      // validation
      if (this.config.flushInterval <= CloudWatchLogger.CLOUD_WATCH_RATE_LIMIT) {
        throw new Error(
          `Flush interval must be greater than ${CloudWatchLogger.CLOUD_WATCH_RATE_LIMIT}ms --> CloudWatch Rate Limit`,
        )
      }

      const ready = this.getOrCreateLogStream(clientIdService)
      this.setupLogStream(ready)
    }
  }

  /**
   * Log a message to CloudWatch with buffering logic.
   * Events below the configured log level are buffered and flushed when an ERROR occurs.
   * Does nothing if LogLevel is OFF.
   */
  logMessage(level: LogLevel, context: string, timestamp: Date, args: any[]): void {
    if (this.config.logLevel === LogLevel.OFF) {
      return
    }

    const logEvent = this.buildLogEvent(level, context, timestamp, args)

    // if level is below threshold, buffer the event
    if (level < this.config.logLevel) {
      pushToRingBuffer(this.pendingBuffer, logEvent, this.bufferSize)
      return
    }

    // on ERROR: flush buffered events first, then clear buffer
    if (level === LogLevel.ERROR) {
      for (const bufferedEvent of this.pendingBuffer) {
        this.logsSubject.next(bufferedEvent)
      }
      this.pendingBuffer = []
    }

    this.logsSubject.next(logEvent)
  }

  private buildLogEvent(level: LogLevel, context: string, dTimestamp: Date, args: any[]): LogEvent {
    // Clone args to avoid mutating the original array
    const argsCopy = [...args]
    const logDataObject: Record<string, any> = {
      level: getEnumKeyFromNum(LogLevel, level),
      context,
      requestInfo: this.logRequestInfoProvider ? this.logRequestInfoProvider.getRequestInfo() : {},
    }

    if (isError(argsCopy[0])) {
      const err: Error = argsCopy.shift()
      logDataObject.message = `${err.name}: ${err.message}`
      // error case
      if ('stack' in err) {
        logDataObject.error = err['stack']
      }
    } else if (typeof argsCopy[0] === 'string') {
      logDataObject.message = argsCopy.shift()
    }
    if (argsCopy.length > 0) {
      logDataObject.data = argsCopy.length === 1 ? argsCopy[0] : argsCopy
    }

    return {
      message: JSON.stringify(logDataObject, this.jsonStringifyReplacer),
      timestamp: dTimestamp.getTime(),
    }
  }

  private getOrCreateLogStream({ createdInThisSession, clientId }: ClientIdService): Observable<void> {
    // return observable to keep it cold
    return defer(async () => {
      // if the clientId was created in this session, no LogStream can exist of it.
      if (createdInThisSession) {
        await this.api.createLogStream(clientId)
        return
      }
      try {
        await this.api.describeLogStream(clientId)
      } catch (error) {
        if (error instanceof HttpApiError && error.statusCode === 404) {
          await this.api.createLogStream(clientId)
          return
        }
        throw error
      }
    })
  }

  private setupLogStream(ready$: Observable<void>): void {
    // actual log subscription
    this.logsSubject
      .pipe(
        // buffer logs --> wait for ready$ to complete - then setup the time based interval scheduler
        buffer(
          ready$.pipe(
            first(),
            mergeMap(() => timer(0, this.config.flushInterval)),
          ),
        ),

        // only none empty arrays
        filter((logEvents) => logEvents?.length > 0),

        // put logs to cloudwatch
        mergeMap(this.putLogEvents),
      )
      .subscribe({ error: console.error.bind(console) })
  }

  private readonly putLogEvents = (events: LogEvent[]): Observable<void> => {
    // outer observable only for retry logic -- see below
    return defer(() => this.api.writeLogs(this.clientId, events)).pipe(
      catchError((err) => {
        console.warn('unable to put logs to CloudWatch --> we try again with the next batch', err)
        return of(void 0)
      }),
    )
  }
}
